import { useEffect, useState } from "react";
import { Link, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { t } from "../lib/i18n";
import { toast } from "sonner";
import { Lightning } from "@phosphor-icons/react";

// Iter 33 — accept only relative in-app paths in `?next=` so we can't be
// used as an open redirect. Anything else falls back to role default.
const _safeNext = (raw) => {
  if (!raw) return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
};

export default function Login() {
  const { user, login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const next = _safeNext(new URLSearchParams(location.search).get("next"));

  const destFor = (role) => next || (role === "admin" ? "/admin" : "/me");

  useEffect(() => {
    if (user && user !== false) {
      nav(destFor(user.role), { replace: true });
    }
  }, [user, nav]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user && user !== false) {
    return <Navigate to={destFor(user.role)} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const data = await login(email, password);
    setBusy(false);
    if (data) {
      toast.success(t.loginSuccess);
      const dest = destFor(data.role);
      nav(dest, { replace: true });
      setTimeout(() => {
        if (window.location.pathname === "/login") window.location.href = dest;
      }, 200);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Lightning size={32} weight="fill" className="text-[#FF0055] mx-auto" />
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF0055] font-mono mt-3">
            // BEYX.PL
          </div>
          <h1 className="font-display font-black uppercase italic text-5xl mt-2">
            {t.loginTitle}
          </h1>
          <p className="text-white/50 text-sm mt-2">{t.loginSub}</p>
        </div>

        <form onSubmit={submit} className="border border-white/10 bg-[#0C0C14] p-6 space-y-4" data-testid="login-form">
          <Input label={t.email} type="email" value={email} onChange={setEmail} testid="login-email" />
          <Input label={t.password} type="password" value={password} onChange={setPassword} testid="login-password" />
          {error && (
            <div data-testid="login-error" className="text-xs text-[#FF0055] font-mono">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full px-6 py-3 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.25em] text-sm transition-colors disabled:opacity-50"
          >
            {busy ? t.authenticating : t.signIn}
          </button>
          <div className="text-xs text-white/50 text-center pt-2">
            <Link
              to="/forgot-password"
              className="text-[#00E5FF] hover:underline"
              data-testid="forgot-password-link"
            >
              {t.forgotPassword}
            </Link>
          </div>
          <div className="text-xs text-white/50 text-center">
            {t.noAccount}{" "}
            <Link to="/register" className="text-[#00E5FF] hover:underline" data-testid="to-register-link">
              {t.toRegister}
            </Link>
          </div>
        </form>

        {/* Iter 41 — Emergent-managed Google sign-in.
            REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
        <div className="mt-5">
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-mono text-white/40">{t.or || "or"}</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <button
            type="button"
            data-testid="google-login-btn"
            onClick={() => {
              const redirectUrl = window.location.origin + "/";
              window.location.href =
                "https://auth.emergentagent.com/?redirect=" +
                encodeURIComponent(redirectUrl);
            }}
            className="w-full flex items-center justify-center gap-3 border border-white/20 bg-white/5 hover:bg-white/10 px-4 py-3 text-sm uppercase tracking-[0.2em] font-bold"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.7 1.22 9.19 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              <path fill="none" d="M0 0h48v48H0z"/>
            </svg>
            {t.signInWithGoogle || "Sign in with Google"}
          </button>
          <p className="text-[10px] font-mono text-white/40 mt-2 text-center">
            {t.googleAuthHint || "Powered by Emergent — one click, no password to remember."}
          </p>
        </div>
      </div>
    </div>
  );
}

function Input({ label, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-2">
        {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-[#00E5FF]"
      />
    </div>
  );
}
